<template>
  <v-navigation-drawer
    v-model="drawer"
    mobile-breakpoint="sm"
    :rail="$vuetify.display.smAndDown"
    :rail-width="0"
    :location="$vuetify.display.smAndDown ? 'bottom' : 'right'"
    :temporary="$vuetify.display.smAndDown"
    :permanent="!$vuetify.display.smAndDown"
    class="accesslink-pod-sidemenu"
    width="480"
    style=""
    ref="drawerElement"
    :aria-label="$t('routes.accesslink.mediaInformation')"
  >
    <v-btn-toggle
      v-model="activeToolbar"
      mandatory
      max-width="100%"
      rounded="lg"
      class="d-flex"
      color="primary"
    >
      <v-btn
        class="flex-grow-1"
        variant="tonal"
        :rounded="false"
        :value="0"
        :aria-label="$t('routes.accesslink.mediaInformation')"
        :aria-pressed="activeToolbar === 0"
        style="border-radius: unset;"
        icon="mdi-information"
      />
      <v-btn
        v-if="commentsAllowed"
        variant="tonal"
        class="flex-grow-1"
        :rounded="false"
        :value="1"
        :aria-label="$t('routes.accesslink.comments')"
        :aria-pressed="activeToolbar === 1"
        style="border-radius: unset;"
        icon="mdi-comment"
      />
      <v-btn
        v-if="showAssets || showSubtitles"
        variant="tonal"
        class="flex-grow-1"
        :rounded="false"
        :value="2"
        :aria-label="$t('routes.accesslink.assets')"
        :aria-pressed="activeToolbar === 2"
        style="border-radius: unset;"
        icon="mdi-image-multiple-outline"
      />
    </v-btn-toggle>

    <div v-if="pod" class="accesslink-pod-sidemenu-content-wrapper">
      <InformationToolbar
        v-if="activeToolbar === 0"
        :pod="pod"
        :disableNotes="true"
      />
      <AccesslinkComments
        v-if="commentsAllowed && activeToolbar === 1"
        :pod="pod"
        @commentTimeFocus="(time) => $emit('commentTimeFocus', time)"
      />
      <AccesslinkAssets
        v-if="(showAssets || showSubtitles) && activeToolbar === 2"
        :pod="pod"
        :downloadsAllowed="downloadsAllowed"
        :showOnlySubtitles="showSubtitles"
      />
    </div>
  </v-navigation-drawer>
</template>

<script>
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import MimetypeHelper from '../../../../components/MimetypeHelper.js'

import InformationToolbar from '../../../../layouts2/mainLayout/components/podSidemenu/informationToolbar/InformationToolbar.vue'
import AccesslinkComments from './comments/AccesslinkComments.vue'
import AccesslinkAssets from './AccesslinkAssets.vue'
import { useCommonStore } from '@/stores/CommonStore.js';

export default {
  name: "AccesslinkPodSidemenu",
  components: {
    InformationToolbar,
    AccesslinkComments,
    AccesslinkAssets,
  },
  props: {
    value: {
      type: Boolean,
      required: false,
    },
    pod: {
      type: Object,
      required: false,
    },
    parentPod: {
      type: Object,
      required: false,
    },
  },
  emits: ['commentTimeFocus', "input"],
  data() {
    return {
      activeToolbar: 0,
    };
  },
  mounted() {
    this.setDefaultActiveToolbar();
  },
  watch: {
    commentsAllowed() {
      this.setDefaultActiveToolbar();
    },
    showAssets() {
      this.setDefaultActiveToolbar();
    }
  },
  methods: {
    setDefaultActiveToolbar() {
      if (this.commentsAllowed) {
        this.activeToolbar = 1;
      } else if (this.showAssets) {
        this.activeToolbar = 2;
      } else {
        this.activeToolbar = 0;
      }
    }
  },
  computed: {
    drawer: {
      set(v) {
        this.$emit("input", v);
      },
      get() {
        return this.value;
      }
    },
    isVideoPod: function() {
      return MimetypeHelper.isMimetypeVideo(this.pod);
    },
    isAudioPod: function() {
      return MimetypeHelper.isMimetypeAudio(this.pod);
    },
    isImagePod: function() {
      return MimetypeHelper.isMimetypeImage(this.pod);
    },

    getAccesslinkInfo: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkInfo;
    },
    showAssets: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.params) {
        return this.getAccesslinkInfo.params.showassets;
      }
      return false;
    },
    showSubtitles: function() {
      const isEnabled = this.getAccesslinkInfo?.params?.showsubtitles ?? false;
      if (!isEnabled) {
        return false;
      }

      const commonStore = useCommonStore();

      let hasAssetSubtitle = false;
      for (const assetType in this.pod.asset) {
        for (const anAsset of this.pod.asset[assetType]) {
          if (anAsset.type === commonStore.assetTypes.SRT || anAsset.type === commonStore.assetTypes.VTT) {
            hasAssetSubtitle = true;
            break;
          }
        }
      }

      return hasAssetSubtitle;
    },
    showVersions() {
      const addVersions = this.getAccesslinkInfo?.params?.addversions === 1;
      if (addVersions) {
        if (Array.isArray(this.parentPod?.childrenpods) && this.parentPod.childrenpods.length > 0) {
          return true;
        }
      }

      return false;
    },
    isTheContextPodIsParentPod: function() {
      return this.parentPod?.id === this.pod?.id;
    },
    commentsAllowed: function() {
      // comments are allowed only for video, audio and image pods
      if (!this.isVideoPod && !this.isAudioPod && !this.isImagePod) {
        return false;
      }

      // comments are allowed only if the accesslink allows comments
      const commentsAllowedParam = this.getAccesslinkInfo?.params?.allowcomments === 1;
      if (!commentsAllowedParam) {
        return false;
      }

      // comments are allowed only for parent pod
      if (this.showVersions && !this.isTheContextPodIsParentPod) {
        return false;
      }

      return commentsAllowedParam;
    },
    downloadsAllowed: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.params) {
        return this.getAccesslinkInfo.params.allowdownloading;
      }

      return false;
    },
  }
}
</script>

<style lang="scss" scoped>
.accesslink-pod-sidemenu {
  top: 70px !important;
  margin-top: 10px;
  margin-left: 4px;
  border-radius: 7px;
  max-height: calc(100% - 84px) !important;
  padding: 8px;

  background-color: rgb(var(--v-theme-tw-surface)) !important;

  &-content-wrapper {
    padding: 10px;
  }

  /* &.v-theme--dark {
    background-color: #232b36 !important;
  }

  &.v-theme--light {
    background-color: #FFFFFF !important;
  } */
}

@media only screen and (max-width: 960px) {
  .accesslink-pod-sidemenu {
    margin-left: 0;
    border-radius: 0;
  }

  .accesslink-pod-sidemenu.v-navigation-drawer--active {
    height: 100vh !important;
  }
}
</style>
